import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dumbbell, StickyNote, ListChecks } from "lucide-react";

const TYPE_LABEL = {
  load: { es: "Carga (lbs)", en: "Load (lbs)", fr: "Charge (lbs)", ph: "Ej: 185 lbs" },
  time: { es: "Tiempo", en: "Time", fr: "Temps", ph: "Ej: 12:45" },
  reps: { es: "Repeticiones", en: "Reps", fr: "Répétitions", ph: "Ej: 120" },
  free: { es: "Resultado", en: "Result", fr: "Résultat", ph: "..." },
};

function VideoEmbed({ v }) {
  if (v.provider === "youtube" || v.provider === "vimeo") {
    return (
      <div className="mt-3 aspect-video w-full overflow-hidden rounded-xl border border-border">
        <iframe src={v.embed_url} title={v.title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    );
  }
  return <a href={v.url} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm font-medium text-primary underline">{v.title || v.url}</a>;
}

export function MyProgram() {
  const { t, tl, lang } = useI18n();
  const [programs, setPrograms] = useState(null);
  const [pi, setPi] = useState(0);
  const [wi, setWi] = useState(0);
  const [di, setDi] = useState(0);
  const [tab, setTab] = useState("prog");
  const [values, setValues] = useState({});
  const [note, setNote] = useState("");

  const load = async () => {
    const { data } = await api.get("/me/programs");
    setPrograms(data);
  };
  useEffect(() => { load(); }, []);

  const prog = programs && programs[pi];
  const week = prog && prog.weeks[wi];
  const day = week && week.days[di];

  useEffect(() => {
    if (day) { setValues(day.my_result?.values || {}); setNote(day.my_result?.client_note || ""); }
  }, [prog?.program_id, wi, di, programs]);

  const submit = async () => {
    try {
      await api.post(`/programs/${prog.program_id}/days/${day.day_id}/result`, { values, client_note: note });
      toast.success(t("common.success"));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AppLayout>
      <h1 className="mb-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
        <Dumbbell className="h-7 w-7 text-primary" /> {tl({ es: "Mi programa", en: "My program", fr: "Mon programme" })}
      </h1>

      {programs === null ? (
        <div className="py-16 text-center text-muted-foreground">{t("common.loading")}</div>
      ) : programs.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          {tl({ es: "Aún no tienes un programa asignado.", en: "No program assigned yet.", fr: "Aucun programme ne t'a encore été assigné." })}
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* program selector */}
          {programs.length > 1 && (
            <div className="flex flex-wrap gap-2">
              {programs.map((p, idx) => (
                <button key={p.program_id} data-testid={`prog-tab-${idx}`} onClick={() => { setPi(idx); setWi(0); setDi(0); }}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium ${idx === pi ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{p.title}</button>
              ))}
            </div>
          )}

          <div>
            <h2 className="font-display text-2xl font-bold">{prog.title}</h2>
            {prog.description && <p className="mt-1 text-sm text-muted-foreground">{prog.description}</p>}
          </div>

          {/* week tabs */}
          <div className="flex flex-wrap gap-2">
            {prog.weeks.map((w, idx) => (
              <button key={w.week_id} data-testid={`week-tab-${idx}`} onClick={() => { setWi(idx); setDi(0); }}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold ${idx === wi ? "bg-royal text-white" : "bg-secondary"}`}>
                {w.label || `${tl({ es: "Semana", en: "Week", fr: "Semaine" })} ${idx + 1}`}
              </button>
            ))}
          </div>

          {/* day tabs */}
          {week && (
            <div className="flex flex-wrap gap-2">
              {week.days.map((d, idx) => (
                <button key={d.day_id} data-testid={`day-tab-${idx}`} onClick={() => setDi(idx)}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${idx === di ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                  {d.label || `${tl({ es: "Día", en: "Day", fr: "Jour" })} ${idx + 1}`}
                </button>
              ))}
            </div>
          )}

          {day && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-display text-xl font-semibold">{day.label}</h3>
              {/* sub tabs: programme / coach note */}
              <div className="mt-3 flex gap-2 border-b border-border">
                <button data-testid="subtab-prog" onClick={() => setTab("prog")} className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${tab === "prog" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
                  <ListChecks className="h-4 w-4" /> {tl({ es: "Programa", en: "Program", fr: "Programme" })}
                </button>
                <button data-testid="subtab-note" onClick={() => setTab("note")} className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${tab === "note" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>
                  <StickyNote className="h-4 w-4" /> Coach note
                </button>
              </div>

              {tab === "prog" ? (
                <div className="mt-4 space-y-5">
                  {(day.blocks || []).map((b, i) => {
                    const rt = TYPE_LABEL[b.result_type] || TYPE_LABEL.free;
                    return (
                      <div key={b.block_id} data-testid={`prog-block-${b.block_id}`} className="rounded-xl border border-border bg-background/50 p-4">
                        <div className="flex items-center gap-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{i + 1}</span>
                          <h4 className="font-display text-base font-semibold">{b.title}</h4>
                        </div>
                        {b.description && <p className="mt-2 whitespace-pre-wrap text-sm text-muted-foreground">{b.description}</p>}
                        {(b.videos || []).map((v, vi) => <VideoEmbed key={v.video_id || vi} v={v} />)}
                        <div className="mt-3">
                          <label className="text-sm font-medium">{rt[lang]}</label>
                          <Input data-testid={`prog-value-${b.block_id}`} value={values[b.block_id] || ""} onChange={(e) => setValues((p) => ({ ...p, [b.block_id]: e.target.value }))} placeholder={rt.ph} className="mt-1.5 h-11" />
                        </div>
                      </div>
                    );
                  })}
                  <div>
                    <label className="text-sm font-medium">{tl({ es: "Mi comentario", en: "My feedback", fr: "Mon retour" })}</label>
                    <Textarea data-testid="prog-feedback" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder={tl({ es: "¿Cómo te fue?", en: "How did it go?", fr: "Comment ça s'est passé ?" })} />
                  </div>
                  <Button data-testid="prog-submit" onClick={submit} className="rounded-full">{t("common.save")}</Button>
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  {day.coach_note ? (
                    <div className="rounded-xl border border-gold/40 bg-gold/5 p-4 text-sm whitespace-pre-wrap">{day.coach_note}</div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{tl({ es: "Sin nota del coach.", en: "No coach note.", fr: "Pas de note du coach." })}</p>
                  )}
                  <div>
                    <label className="text-sm font-medium">{tl({ es: "Mi comentario", en: "My feedback", fr: "Mon retour" })}</label>
                    <Textarea data-testid="note-feedback" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" />
                    <Button data-testid="note-submit" onClick={submit} className="mt-3 rounded-full">{t("common.save")}</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
